import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import * as XLSX from 'xlsx';
import { platformAPI } from '../../../services/platformAPI';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import { MASTER_UPLOADS, describeMaster } from './masterUploads';
import { loadOrganisationSettings } from '../../../utils/organisationSettings';
import './MastersPage.scss';
import './CustomerMaster.scss';

const headerKey = (master, header) => {
  const text = String(header || '').trim().toLowerCase();
  const match = master.columns.find(([, label]) => label.toLowerCase() === text);
  return match ? match[0] : '';
};

const cellValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }
  if (value == null) return '';
  return value;
};

const readWorkbook = async (file, master) => {
  const name = String(file.name || '').toLowerCase();
  if (name.endsWith('.csv') || file.type === 'text/csv') {
    throw new Error('Upload an Excel workbook (.xlsx). CSV files are not accepted.');
  }
  if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
    throw new Error('Upload an Excel workbook (.xlsx).');
  }
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames.find((item) => item.toLowerCase() === 'data') || workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error('The workbook has no Data sheet.');
  const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true });
  const headers = (grid[0] || []).map((cell) => String(cell || '').trim());
  const keys = headers.map((header) => headerKey(master, header));
  if (!keys.some(Boolean)) {
    throw new Error('This file does not use the Excel template. Download the template and keep the column headings.');
  }
  const rows = [];
  grid.slice(1).forEach((line, index) => {
    const record = { _row: index + 2 };
    let filled = false;
    keys.forEach((key, column) => {
      if (!key) return;
      const value = cellValue(line[column]);
      if (String(value).trim() !== '') filled = true;
      record[key] = value;
    });
    if (filled) rows.push(record);
  });
  if (!rows.length) throw new Error('The Excel file has no data rows.');
  return rows;
};

const downloadTemplate = (master) => {
  const headers = master.columns.map(([, label]) => label);
  const dataSheet = XLSX.utils.aoa_to_sheet([headers]);
  dataSheet['!cols'] = headers.map((label) => ({ wch: Math.max(label.length + 4, 18) }));
  const instructions = [
    [`${master.title} upload`],
    ['Fill the Data sheet. Do not rename the column headings.'],
    ['Save the file as Excel (.xlsx) and upload it here. CSV is not accepted.'],
    [],
    ...master.notes.map((line) => [line])
  ];
  const infoSheet = XLSX.utils.aoa_to_sheet(instructions);
  infoSheet['!cols'] = [{ wch: 110 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, dataSheet, 'Data');
  XLSX.utils.book_append_sheet(workbook, infoSheet, 'Instructions');
  XLSX.writeFile(workbook, master.fileName);
};

const UploadCard = ({ master }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('create');

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const rows = await readWorkbook(file, master);
      const response = await platformAPI.importMasterRows(master.id, rows, mode);
      setResult({
        tone: 'success',
        message: response.data?.message || 'The rows were added.',
        errors: []
      });
    } catch (err) {
      const data = err.response?.data;
      setResult({
        tone: 'danger',
        message: data?.message || err.message || 'The file could not be uploaded.',
        errors: data?.data?.errors || []
      });
    } finally {
      setBusy(false);
    }
  };

  const Icon = master.icon;

  return (
    <article className="mst-catalog__card mst-upload__card">
      <div className="mst-catalog__card-head">
        <span className="mst-catalog__icon" aria-hidden><Icon /></span>
        <h2>{master.title}</h2>
      </div>
      <p>{master.description}</p>
      <label className="small text-muted d-block mb-2">
        Upload behaviour
        <select className="form-select form-select-sm mt-1" aria-label={`Upload behaviour for ${master.title}`} value={mode} onChange={(event) => setMode(event.target.value)}>
          <option value="create">Create new rows</option>
          <option value="update">Update matching rows</option>
        </select>
      </label>
      <div className="mst-upload__actions">
        <Button size="sm" variant="outline-primary" type="button" onClick={() => downloadTemplate(master)} disabled={busy}>
          Download template
        </Button>
        <Button size="sm" variant="primary" type="button" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? <Spinner animation="border" size="sm" /> : 'Upload Excel'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          hidden
          onChange={upload}
        />
      </div>
      {result ? (
        <Alert variant={result.tone} className="mst-upload__alert">
          <div>{result.message}</div>
          {result.errors.length ? (
            <ul>
              {result.errors.slice(0, 8).map((item) => (
                <li key={`${item.row}-${item.message}`}>Row {item.row}: {item.message}</li>
              ))}
            </ul>
          ) : null}
        </Alert>
      ) : null}
    </article>
  );
};

const MasterUploadPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [masters, setMasters] = useState(MASTER_UPLOADS);
  useDocumentTitle('Upload Center');
  useEffect(() => {
    loadOrganisationSettings().then((data) => {
      const lists = data?.settings?.importLists;
      if (lists) setMasters(MASTER_UPLOADS.map((master) => describeMaster(master, lists)));
    });
  }, []);

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  return (
    <div className="mst-catalog">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}>
        <BiArrowBack />
        Masters
      </button>
      <WorkspacePageHeader
        title="Upload Center"
        help="masters"
        subtitle="Download the Excel template for a master, fill the Data sheet, and upload it. The rows are added only when every row is valid."
      />
      <div className="mst-catalog__grid mst-upload__grid">
        {masters.map((master) => (
          <UploadCard key={master.id} master={master} />
        ))}
      </div>
    </div>
  );
};

export default MasterUploadPage;
